import type { OrderFragment, OrderLineFragment } from '#graphql-operations'
import { colorHex } from '~/constants/colorAliases'
import { isGiftOrderLine } from '~/utils/price'
import { isSizeGroup } from '~/utils/size'

const COLOR_FACET_CODE = 'color'

/** A claimed cart item can still be removed or have its quantity changed; fixed gifts stay locked. */
export function isEditableGiftOrderLine(
  line: Pick<OrderLineFragment, 'customFields'>,
  progress: readonly Pick<OrderFragment['giftProgress'][number], 'ruleId' | 'rewardMode'>[] = [],
): boolean {
  const ruleId = line.customFields?.giftRuleId
  return ruleId != null && progress.some(entry =>
    String(entry.ruleId) === String(ruleId) && entry.rewardMode === 'CHEAPEST_IN_COLLECTION')
}

interface OrderLineColorSource {
  productVariant: {
    product: {
      facetValues: {
        code: string
        name?: string | null
        facet?: { code: string } | null
      }[]
    }
  }
}

/**
 * The colourway of the product an order line was placed for, e.g. "Midnight
 * Blue".
 *
 * Colour is not a product option on this catalog — every colour is its own
 * product, and the variants only carry size — so it can't be read off
 * `productVariant.options`/`name` the way size can. The product's `color` facet
 * value is the shade the PDP names, whereas the *variant*-level `color` facet
 * holds the coarse bucket ("Beige" for a "Desert" product), so only the
 * product-level value is read here.
 *
 * Some facet values were entered with trailing whitespace ("Noir Black "), so
 * the name is trimmed before display.
 */
export function orderLineColorLabel(line: OrderLineColorSource): string | undefined {
  const name = orderLineColorValue(line)?.name?.trim()
  return name || undefined
}

/** The product-level `color` facet value an order line's colourway comes from. */
function orderLineColorValue(line: OrderLineColorSource) {
  return line.productVariant.product.facetValues
    ?.find(facetValue => facetValue.facet?.code === COLOR_FACET_CODE)
}

/**
 * Preview hex for that colourway, for the swatch shown beside the name — the
 * same dot the look piece rows put in front of their colour.
 *
 * Resolved from the facet value's `code`, never its `name`: the code is
 * locale-independent, which is what `colorHex`'s lookup table is keyed on. The
 * shade names happen to be untranslated today (they are brand names — "Almond
 * Blush" is the same string in all five locales), so reading the label would
 * work right now and break silently the first time someone translates one.
 * Dashes are stripped for the same reason the filter drawer strips them
 * (`collection/Filters.vue`): 99 of the 136 colour codes are hyphenated
 * (`baby-blue`) and the table's keys are not.
 *
 * `colorHex` answers `#cccccc` for a shade it does not know — 3 of the 136
 * today — so the dot degrades to neutral grey rather than disappearing, which
 * is how every other swatch in the app behaves.
 */
export function orderLineColorSwatch(line: OrderLineColorSource): string | undefined {
  const code = orderLineColorValue(line)?.code
  return code ? colorHex(code.replace(/-/g, '')) : undefined
}

/**
 * The cart's products held in more than one size (two lines of one product for different
 * variants; gift lines left out), each with the id of its last line in `lines`' order: where the
 * cart drawer offers the size advisor (`CartSizeAdvisorHint`) to a shopper ordering sizes to try.
 * A colour is its own product here, so two colours of a garment are not counted.
 */
export function productsInSeveralSizes(lines: readonly OrderLineFragment[]): Map<string, OrderLineFragment['id']> {
  const variants = new Map<string, Set<string>>()
  const lastLine = new Map<string, OrderLineFragment['id']>()
  for (const line of lines) {
    if (isGiftOrderLine(line))
      continue
    const productId = String(line.productVariant.product.id)
    const seen = variants.get(productId) ?? new Set<string>()
    seen.add(String(line.productVariant.id))
    variants.set(productId, seen)
    lastLine.set(productId, line.id)
  }
  return new Map([...lastLine].filter(([productId]) => (variants.get(productId)?.size ?? 0) > 1))
}

/** The size option of the variant an order line was placed for, if it has one. */
export function orderLineSizeOption(line: OrderLineFragment) {
  return line.productVariant.options.find(option => isSizeGroup(option.group.name, option.group.code))
}

/** Only a sold-out line can request a reminder; a quantity shortage is still purchasable. */
export function orderLineReminderSizes(line: OrderLineFragment): { variantId: string, label: string }[] {
  if (line.productVariant.stockLevel !== 'OUT_OF_STOCK')
    return []
  return [{
    variantId: String(line.productVariant.id),
    label: orderLineSizeOption(line)?.name ?? line.productVariant.options.map(option => option.name).join(' / '),
  }]
}

/**
 * Whether the shopper can switch an order line to another size of the same
 * product from the cart (`useOrderLineSizeSwap`). Not a gift card, whose "sizes" are denominations, so
 * a swap would change what is bought, not how it fits; and only a variant that
 * has a size at all.
 */
export function canChangeOrderLineSize(line: OrderLineFragment): boolean {
  const isGiftCard = line.productVariant.product.facetValues
    .some(facetValue => facetValue.facet?.code === 'category' && facetValue.code === 'gift-card')
  return !isGiftCard && Boolean(orderLineSizeOption(line))
}
